import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, ArrowUpRight, Landmark, Download,
  Check, CheckCircle2, Loader2, Lock, ExternalLink, Sparkles,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useAuth, beginGoogleAuth } from "@/lib/AuthContext";
import { getToolsForTier } from "@/lib/funding_tools";
import FinalCTA from "@/components/FinalCTA";

const STEP_INTRO = 0;
const STEP_CHECKLIST = 1;
const STEP_EMAIL = 2;
const STEP_RESULT = 3;

export default function FundingWorksheet() {
  const [step, setStep] = useState(STEP_INTRO);
  const [sections, setSections] = useState([]);
  const [sectionIdx, setSectionIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState({ name: "", email: "", business_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");

  useEffect(() => {
    api.get("/funding-checklist").then((r) => setSections(r.data.sections)).catch(() => {});
  }, []);

  const totalQuestions = sections.reduce((a, s) => a + s.questions.length, 0);
  const answeredCount = Object.keys(answers).length;
  const progressPct = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const toggle = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));
  const currentSection = sections[sectionIdx];
  const sectionComplete = currentSection && currentSection.questions.every((q) => answers[q.id] !== undefined);
  const isLastSection = sectionIdx === sections.length - 1;

  const submit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/funding-leads", {
        name: lead.name,
        email: lead.email,
        business_name: lead.business_name,
        answers,
      });
      setResult(data.result);
      setPdfUrl(`${API}${data.pdf_url.replace(/^\/api/, "")}`);
      setStep(STEP_RESULT);
      trackEvent("funding_readiness_completed", {
        meta: { tier: data.result.tier, percent: String(data.result.percent) },
      });
      toast.success("Report ready!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="relative bg-[var(--tmg-ivory)] border-b border-[var(--tmg-line)] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 pt-16 pb-20 sm:pt-24 sm:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="tmg-overline-gold mb-6 tmg-fade-up flex items-center gap-2">
              <Landmark className="w-3.5 h-3.5" /> FREE WORKSHEET · 4-MIN ASSESSMENT
            </p>
            <h1 data-testid="funding-headline" className="font-display text-[2.5rem] sm:text-5xl lg:text-[4rem] font-semibold leading-[1.04] tracking-tight tmg-fade-up-d1">
              The Business Funding{" "}
              <span className="italic font-light text-[var(--tmg-gold-soft)]">Readiness</span> Worksheet.
            </h1>
            <p className="mt-8 text-base sm:text-lg text-[var(--tmg-muted)] max-w-xl leading-relaxed tmg-fade-up-d2">
              Answer 23 quick questions. See exactly where your business stands on the
              funding readiness ladder — and the right next moves before you apply. Plus
              a branded PDF roadmap delivered instantly.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 tmg-fade-up-d3">
              {step === STEP_INTRO ? (
                <button data-testid="funding-start-btn" onClick={() => { trackEvent("funding_readiness_started"); setStep(STEP_CHECKLIST); }} className="tmg-btn-primary">
                  Start the Worksheet <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <a href="#assessment" className="tmg-btn-secondary">Continue Where You Left Off</a>
              )}
              <Link to="/book-call" className="tmg-btn-secondary">Skip — Book a Call</Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-xs text-[var(--tmg-muted)] font-mono-sm uppercase tracking-[0.18em]">
              <span className="flex items-center gap-2"><Check className="w-3 h-3 text-[var(--tmg-gold)]" /> 23 questions</span>
              <span className="flex items-center gap-2"><Check className="w-3 h-3 text-[var(--tmg-gold)]" /> 4 readiness tiers</span>
              <span className="flex items-center gap-2"><Check className="w-3 h-3 text-[var(--tmg-gold)]" /> Tier-specific tools</span>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <FundingPreviewCard />
          </div>
        </div>
      </section>

      <section id="assessment" className="tmg-section">
        <div className="tmg-container max-w-4xl">
          {step !== STEP_INTRO && step !== STEP_RESULT && (
            <div className="mb-10">
              <div className="flex items-end justify-between mb-3">
                <p className="tmg-overline">
                  {step === STEP_CHECKLIST
                    ? `Section ${sectionIdx + 1} of ${sections.length || "—"}`
                    : "— One Last Step"}
                </p>
                <p className="font-mono-sm text-xs text-[var(--tmg-muted)]">{answeredCount} / {totalQuestions}</p>
              </div>
              <Progress value={progressPct} className="h-1 bg-[var(--tmg-line)]" />
            </div>
          )}

          {step === STEP_INTRO && <FundingIntro onStart={() => { trackEvent("funding_readiness_started"); setStep(STEP_CHECKLIST); }} sections={sections} />}

          {step === STEP_CHECKLIST && currentSection && (
            <div data-testid="funding-checklist-step" className="bg-white border border-[var(--tmg-line)] p-8 sm:p-12 tmg-fade-up">
              <p className="tmg-overline-gold mb-4">— {currentSection.title}</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-10">
                Answer honestly — this is for your insight, not a credit check.
              </h2>
              <div className="space-y-4">
                {currentSection.questions.map((q, i) => (
                  <div key={q.id} data-testid={`funding-q-${q.id}`} className="border border-[var(--tmg-line)] p-6 hover:border-[var(--tmg-gold)]/40 transition-colors">
                    <div className="flex items-start justify-between gap-6 flex-col sm:flex-row">
                      <div className="flex items-start gap-4 flex-1">
                        <span className="font-mono-sm text-xs text-[var(--tmg-gold)] mt-1">{String(i + 1).padStart(2, "0")}</span>
                        <p className="text-base sm:text-lg leading-snug">{q.text}</p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <AnswerBtn active={answers[q.id] === true} onClick={() => toggle(q.id, true)} testId={`funding-answer-${q.id}-yes`}>Yes</AnswerBtn>
                        <AnswerBtn active={answers[q.id] === false} onClick={() => toggle(q.id, false)} testId={`funding-answer-${q.id}-no`}>Not Yet</AnswerBtn>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-10 pt-6 border-t border-[var(--tmg-line)]">
                <button onClick={() => sectionIdx === 0 ? setStep(STEP_INTRO) : setSectionIdx((i) => i - 1)} className="text-sm font-semibold text-[var(--tmg-muted)] hover:text-[var(--tmg-obsidian)] flex items-center gap-2" data-testid="funding-back-btn">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={() => isLastSection ? setStep(STEP_EMAIL) : setSectionIdx((i) => i + 1)} disabled={!sectionComplete} data-testid="funding-next-btn" className="tmg-btn-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0">
                  {isLastSection ? "Get My Report" : "Next Section"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === STEP_EMAIL && (
            <form onSubmit={submit} data-testid="funding-email-form" className="bg-white border border-[var(--tmg-line)] p-8 sm:p-12 tmg-fade-up">
              <p className="tmg-overline-gold mb-4">— Almost There</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-6">Where should we send your funding roadmap?</h2>
              <p className="text-[var(--tmg-muted)] leading-relaxed mb-8 max-w-xl">We'll generate your personalized PDF instantly — no waiting, no spam. You'll also see results on-screen immediately.</p>
              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <Label className="tmg-overline">First name *</Label>
                  <Input data-testid="funding-name-input" required value={lead.name} onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label className="tmg-overline">Email *</Label>
                  <Input data-testid="funding-email-input" type="email" required value={lead.email} onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-2 mb-8">
                <Label className="tmg-overline">Business name (optional)</Label>
                <Input data-testid="funding-business-input" value={lead.business_name} onChange={(e) => setLead((l) => ({ ...l, business_name: e.target.value }))} />
              </div>
              <div className="flex items-center justify-between pt-6 border-t border-[var(--tmg-line)]">
                <button type="button" onClick={() => setStep(STEP_CHECKLIST)} className="text-sm font-semibold text-[var(--tmg-muted)] hover:text-[var(--tmg-obsidian)] flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button type="submit" disabled={submitting} data-testid="funding-submit-btn" className="tmg-btn-primary disabled:opacity-60">
                  {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>) : (<>Show My Results <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </div>
              <p className="mt-6 text-xs text-[var(--tmg-muted)]">By submitting, you'll receive your report and occasional emails from TMG Consulting Group. Unsubscribe anytime.</p>
            </form>
          )}

          {step === STEP_RESULT && result && (
            <FundingResult
              result={result}
              pdfUrl={pdfUrl}
              name={lead.name}
              onRetake={() => {
                setAnswers({}); setSectionIdx(0); setStep(STEP_INTRO); setResult(null);
              }}
            />
          )}
        </div>
      </section>

      {/* DISCLAIMER */}
      {step !== STEP_RESULT && (
        <section className="tmg-section bg-[var(--tmg-ivory-2)]/50 border-y border-[var(--tmg-line)] py-12">
          <div className="tmg-container max-w-4xl text-center">
            <p className="tmg-overline mb-3">— IMPORTANT</p>
            <p className="text-sm text-[var(--tmg-muted)] leading-relaxed">
              Funding is never guaranteed. This worksheet is educational and designed to help
              you become more prepared, organized, and informed before seeking lending, grants,
              business credit, or other funding options. Always do your own research and
              consult a licensed financial professional before making credit or funding decisions.
            </p>
          </div>
        </section>
      )}

      {step !== STEP_RESULT && <FinalCTA />}
    </>
  );
}

function AnswerBtn({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={`px-6 py-3 text-sm uppercase tracking-[0.14em] font-semibold border transition-all duration-200 ${active ? "bg-[var(--tmg-obsidian)] text-[var(--tmg-gold)] border-[var(--tmg-obsidian)]" : "bg-white text-[var(--tmg-obsidian)] border-[var(--tmg-line)] hover:border-[var(--tmg-obsidian)]"}`}>
      {children}
    </button>
  );
}

function FundingPreviewCard() {
  return (
    <div className="relative">
      <div aria-hidden className="absolute -inset-3 border border-[var(--tmg-gold)]/40" />
      <div className="relative bg-[var(--tmg-obsidian)] text-white p-8 sm:p-10">
        <p className="tmg-overline-gold mb-2">— Sample Roadmap Preview</p>
        <p className="font-mono-sm text-xs text-white/50 mb-6">PERSONALIZED · INSTANT · PDF</p>
        <div className="bg-white/[0.04] border border-white/10 p-6">
          <div className="flex items-end gap-4 mb-5">
            <div className="font-display text-6xl font-semibold text-[var(--tmg-gold)] leading-none">58%</div>
            <div className="pb-2">
              <p className="tmg-overline-gold mb-1">— Sample Tier</p>
              <p className="font-display text-xl font-semibold">Building Credit</p>
            </div>
          </div>
          <div className="h-px bg-white/10 my-5" />
          <div className="space-y-2 text-xs text-white/70">
            {[
              ["Foundation", "4 / 4"],
              ["Documentation", "3 / 4"],
              ["Financials", "3 / 5"],
              ["Business Credit", "1 / 4"],
              ["Personal", "2 / 3"],
              ["Funding Clarity", "1 / 3"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <span>{k}</span>
                <span className="text-[var(--tmg-gold)] font-mono-sm">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-6 text-xs text-white/50 leading-relaxed">
          Your real report will include your score, tier-specific action steps, and recommended tools.
        </p>
      </div>
    </div>
  );
}

function FundingIntro({ onStart, sections }) {
  return (
    <div className="grid lg:grid-cols-12 gap-12 tmg-fade-up">
      <div className="lg:col-span-7">
        <p className="tmg-overline mb-5">— What You'll Get</p>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-8">
          A clear picture of where your business stands on the funding ladder.
        </h2>
        <ul className="space-y-5 mb-10">
          {[
            ["Your Funding Readiness Score (0–100%)", "See exactly how lender-ready your business is today."],
            ["Your Readiness Tier", "Pre-Foundation, Building Credit, Ready to Apply, or Funding-Ready."],
            ["A 4-Step Action Plan", "The specific moves to make in the next 30 days — based on your tier."],
            ["Recommended Tools for Your Stage", "Curated business credit, monitoring, and funding tools matched to where you are."],
          ].map(([t, d]) => (
            <li key={t} className="flex gap-4">
              <span className="w-8 h-8 bg-[var(--tmg-obsidian)] text-[var(--tmg-gold)] flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4" />
              </span>
              <div>
                <p className="font-display text-lg font-semibold leading-snug">{t}</p>
                <p className="text-sm text-[var(--tmg-muted)] mt-1">{d}</p>
              </div>
            </li>
          ))}
        </ul>
        <button onClick={onStart} className="tmg-btn-primary" data-testid="funding-intro-start-btn">
          Begin — Takes 4 Minutes <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <div className="lg:col-span-5">
        <div className="bg-[var(--tmg-ivory-2)] p-8">
          <p className="tmg-overline-gold mb-6">— What We'll Cover</p>
          <ol className="space-y-4">
            {sections.map((s, i) => (
              <li key={s.id} className="flex items-baseline gap-4 border-b border-[var(--tmg-line)] pb-3 last:border-0 last:pb-0">
                <span className="font-mono-sm text-xs text-[var(--tmg-gold)]">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-base font-medium flex-1">{s.title}</span>
                <span className="font-mono-sm text-xs text-[var(--tmg-muted)]">{s.questions.length}q</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

function FundingResult({ result, pdfUrl, name, onRetake }) {
  const { user } = useAuth();
  const tools = getToolsForTier(result.rec_slug);

  return (
    <div data-testid="funding-result-block" className="tmg-fade-up space-y-0">
      {/* Score header */}
      <div className="bg-[var(--tmg-obsidian)] text-white p-10 sm:p-14 relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 90% 10%, rgba(212,175,55,0.25), transparent 50%)" }} />
        <div className="relative">
          <p className="tmg-overline-gold mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> YOUR ROADMAP IS READY · {result.tier.toUpperCase()}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-2">
            {name?.split(" ")[0] || "Hey"}, here's where you stand.
          </h2>
          <p className="text-white/70 max-w-xl mb-10">
            We've also generated your branded PDF roadmap — download it below for the full action plan.
          </p>
          <div className="grid sm:grid-cols-3 gap-px bg-white/10 mb-10">
            <div className="bg-[var(--tmg-obsidian)] p-8">
              <p className="tmg-overline-gold mb-3">— Score</p>
              <div className="font-display text-6xl font-semibold text-[var(--tmg-gold)] leading-none">{result.percent}%</div>
              <p className="text-xs text-white/60 mt-2 font-mono-sm">{result.yes_count} of {result.total} indicators</p>
            </div>
            <div className="bg-[var(--tmg-obsidian)] p-8">
              <p className="tmg-overline-gold mb-3">— Your Tier</p>
              <div className="font-display text-3xl font-semibold leading-tight">{result.tier}</div>
            </div>
            <div className="bg-[var(--tmg-obsidian)] p-8">
              <p className="tmg-overline-gold mb-3">— Focus Right Now</p>
              <div className="font-display text-xl font-semibold leading-tight text-[var(--tmg-gold)]">{result.recommendation}</div>
            </div>
          </div>
          <p className="text-base sm:text-lg text-white/85 leading-relaxed max-w-3xl">{result.summary}</p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="funding-download-pdf" onClick={() => trackEvent("funding_pdf_downloaded")} className="tmg-btn-primary">
              Download PDF Roadmap <Download className="w-4 h-4" />
            </a>
            <Link to="/book-call" data-testid="funding-result-book-call" className="bg-transparent border border-[var(--tmg-gold)] text-[var(--tmg-gold)] hover:bg-[var(--tmg-gold)] hover:text-[var(--tmg-obsidian)] font-semibold px-7 py-4 text-sm uppercase tracking-[0.14em] transition-colors inline-flex items-center gap-2">
              Book a Strategy Call <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Action plan */}
      <div className="bg-white border border-t-0 border-[var(--tmg-line)] p-10 sm:p-14">
        <p className="tmg-overline mb-5">— Your 30-Day Action Plan</p>
        <h3 className="font-display text-2xl sm:text-3xl font-semibold leading-tight mb-8">
          The next moves for your tier.
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          {result.next_steps.map((step, i) => (
            <div key={i} className="flex gap-4 border border-[var(--tmg-line)] p-5">
              <span className="font-mono-sm text-xs text-[var(--tmg-gold)] mt-1">{String(i + 1).padStart(2, "0")}</span>
              <p className="text-sm leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Save to account banner */}
      {!user && (
        <div className="bg-[var(--tmg-ivory-2)] border-x border-b border-[var(--tmg-line)] p-6 sm:p-8 flex items-center justify-between gap-6 flex-wrap">
          <div>
            <p className="tmg-overline-gold mb-2">— Save Your Roadmap</p>
            <h3 className="font-display text-lg font-semibold leading-tight">
              Sign in to save this roadmap and revisit it anytime.
            </h3>
            <p className="text-sm text-[var(--tmg-muted)] mt-1">Free · Google sign-in.</p>
          </div>
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            onClick={() => beginGoogleAuth("/my-account")}
            data-testid="funding-save-account-btn"
            className="tmg-btn-dark"
          >
            Sign in with Google
          </button>
        </div>
      )}

      {/* Breakdown */}
      <div className="bg-[var(--tmg-ivory-2)]/40 border border-t-0 border-[var(--tmg-line)] p-10 sm:p-14">
        <p className="tmg-overline mb-5">— Your Breakdown</p>
        <h3 className="font-display text-2xl sm:text-3xl font-semibold leading-tight mb-8">
          How you scored across each area.
        </h3>
        <div className="space-y-4">
          {Object.entries(result.section_scores).map(([key, s]) => {
            const pct = Math.round((s.yes / s.total) * 100);
            return (
              <div key={key}>
                <div className="flex justify-between items-baseline mb-1.5">
                  <span className="font-display text-base font-medium capitalize">{key.replace(/_/g, " ")}</span>
                  <span className="font-mono-sm text-xs text-[var(--tmg-muted)]">{s.yes} / {s.total}</span>
                </div>
                <div className="h-1.5 bg-[var(--tmg-line)] overflow-hidden">
                  <div className="h-full bg-[var(--tmg-gold)] transition-all duration-1000" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Affiliate / recommended tools for tier */}
      {tools.length > 0 && (
        <div data-testid="funding-tools-block" className="bg-white border border-t-0 border-[var(--tmg-line)] p-10 sm:p-14">
          <p className="tmg-overline-gold mb-5">— Recommended for Your Tier</p>
          <h3 className="font-display text-2xl sm:text-3xl font-semibold leading-tight mb-3">
            Tools to take your next step.
          </h3>
          <p className="text-[var(--tmg-muted)] mb-10 max-w-2xl text-sm">
            TMG Consulting Group may earn a small commission when you sign up through these links — at no extra cost
            to you. Only tools we actually recommend for your stage.
          </p>
          <div className="grid md:grid-cols-2 gap-px bg-[var(--tmg-line)]">
            {tools.map((tool, i) => (
              <ToolCard key={tool.id} tool={tool} index={i} tier={result.rec_slug} source="funding-result" />
            ))}
          </div>
        </div>
      )}

      {/* Premium Roadmap Upsell */}
      <div className="relative bg-[var(--tmg-obsidian)] text-white p-10 sm:p-14 overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 10% 80%, rgba(212,175,55,0.18), transparent 50%)" }} />
        <div className="relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="tmg-overline-gold mb-5 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> NEXT-LEVEL · 1:1 SUPPORT
            </p>
            <h3 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-6">
              Want the personalized credit-building roadmap?
            </h3>
            <p className="text-white/75 leading-relaxed mb-8 max-w-2xl">
              The free worksheet shows you where you stand. The 1:1 Funding Readiness Strategy
              Call gives you the exact step-by-step plan tailored to your business, your timeline,
              and the funding type you're after — including which trade lines to open in what
              order, which lenders fit your profile, and how to avoid the inquiries that hurt
              your score.
            </p>
            <ul className="space-y-2 mb-8 text-sm text-white/85">
              {[
                "Personalized 90-day credit-building sequence",
                "Lender match suggestions for your profile",
                "Funding-application prep checklist",
                "Review of your business credit reports together",
              ].map((p) => (
                <li key={p} className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-[var(--tmg-gold)] flex-shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <Link to="/book-call" data-testid="premium-roadmap-cta" className="tmg-btn-primary">
              Book a Strategy Call <ArrowUpRight className="w-4 h-4" />
            </Link>
            <p className="mt-4 text-xs text-white/40 font-mono-sm uppercase tracking-[0.18em] flex items-center gap-2">
              <Lock className="w-3 h-3" /> Self-serve credit-building program coming soon
            </p>
          </div>
          <div className="lg:col-span-4">
            <div className="border border-[var(--tmg-gold)]/40 p-8 bg-white/[0.03]">
              <p className="tmg-overline-gold mb-4">— Pricing</p>
              <div className="font-display text-5xl font-semibold text-[var(--tmg-gold)] leading-none mb-3">$297</div>
              <p className="text-xs text-white/60 font-mono-sm mb-6">60–90 min · Per session</p>
              <div className="h-px bg-white/10 mb-6" />
              <p className="text-sm text-white/75 leading-relaxed">
                The fastest way to a clear, lender-aware credit-building roadmap built around
                your real business.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-t-0 border-[var(--tmg-line)] p-10 sm:p-14 flex flex-wrap gap-4 justify-between items-center">
        <button onClick={onRetake} className="text-sm font-semibold text-[var(--tmg-muted)] hover:text-[var(--tmg-obsidian)] underline-offset-4 hover:underline">
          Retake the assessment
        </button>
        <Link to="/offerings" className="tmg-btn-secondary">
          Explore All Offerings <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function ToolCard({ tool, index, tier = "", source = "funding-result" }) {
  const handleClick = () => {
    // Fire-and-forget click tracking. Use sendBeacon when available for reliability.
    const body = JSON.stringify({ tool_id: tool.id, source, tier });
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(`${API}/affiliate-click`, blob);
      } else {
        fetch(`${API}/affiliate-click`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    } catch (_) {}
  };

  return (
    <a
      href={tool.url}
      target="_blank"
      rel="noopener nofollow sponsored noreferrer"
      onClick={handleClick}
      data-testid={`tool-${tool.id}`}
      className="group bg-white p-6 sm:p-8 hover:bg-[var(--tmg-ivory)] transition-all duration-300 block"
    >
      <div className="flex items-start justify-between mb-4">
        <span className="font-mono-sm text-[10px] text-[var(--tmg-gold)] uppercase tracking-[0.18em]">
          {String(index + 1).padStart(2, "0")} · {tool.category}
        </span>
        <ExternalLink className="w-4 h-4 text-[var(--tmg-muted)] group-hover:text-[var(--tmg-gold)] transition-colors" />
      </div>
      <h4 className="font-display text-xl sm:text-2xl font-semibold leading-tight mb-2">
        {tool.name}
      </h4>
      <p className="text-sm text-[var(--tmg-muted)] leading-relaxed mb-5">{tool.blurb}</p>
      <span className="inline-flex items-center gap-2 text-sm font-semibold border-b border-[var(--tmg-obsidian)] pb-0.5 group-hover:border-[var(--tmg-gold)] group-hover:text-[var(--tmg-gold-soft)] transition-colors">
        {tool.cta} <ArrowUpRight className="w-4 h-4" />
      </span>
    </a>
  );
}
